import React, { useState } from 'react';
import { Wrench, Shield, Terminal, Package, BarChart3, CheckCircle, AlertTriangle, Clock } from 'lucide-react';

const PATCH_BLUEPRINTS = [
  { id: 'patch-windows-server', name: 'Windows Server Patch Cycle', description: 'WSUS/SCCM pipeline: pre-patch snapshot, controlled reboot waves, health validation, rollback on failure.', icon: Shield, difficulty: 'advanced', duration: '~2 hours', category: 'Windows', rating: 4.9, usageCount: 3892 },
  { id: 'patch-linux-server', name: 'Linux Server Patch (yum/apt)', description: 'RHEL/Ubuntu/SLES patching: snapshot, update, kernel reboot check, service validation.', icon: Terminal, difficulty: 'intermediate', duration: '~45 min', category: 'Linux', rating: 4.8, usageCount: 2341 },
  { id: 'patch-third-party-apps', name: 'Third-Party Application Patching', description: 'Java, Chrome, Adobe and more via SCCM/Chocolatey with CVE-driven emergency deployment.', icon: Package, difficulty: 'intermediate', duration: '~1 hour', category: 'Applications', rating: 4.7, usageCount: 1567 },
  { id: 'patch-compliance-reporting', name: 'Patch Compliance Reporting', description: 'Weekly scan, executive compliance report, remediation tickets, trend analysis.', icon: BarChart3, difficulty: 'beginner', duration: '~20 min', category: 'Compliance', rating: 4.6, usageCount: 2087 },
];

const STATS = [
  { label: 'Patch Compliance', value: '94%', icon: CheckCircle, color: 'text-emerald-400', bg: 'bg-emerald-500/10' },
  { label: 'Pending Patches', value: '47', icon: AlertTriangle, color: 'text-amber-400', bg: 'bg-amber-500/10' },
  { label: 'Critical CVEs', value: '3', icon: Shield, color: 'text-red-400', bg: 'bg-red-500/10' },
  { label: 'Servers Patched', value: '1,284', icon: Wrench, color: 'text-blue-400', bg: 'bg-blue-500/10' },
];

const DIFFICULTY_COLORS: Record<string, string> = {
  beginner: 'bg-emerald-500/20 text-emerald-300',
  intermediate: 'bg-blue-500/20 text-blue-300',
  advanced: 'bg-purple-500/20 text-purple-300',
};

export function PatchManagementPage() {
  const [activeCategory, setActiveCategory] = useState('All');
  const categories = ['All', 'Windows', 'Linux', 'Applications', 'Compliance'];
  const filtered = activeCategory === 'All' ? PATCH_BLUEPRINTS : PATCH_BLUEPRINTS.filter((b) => b.category === activeCategory);

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-start justify-between">
        <div>
          <div className="flex items-center gap-3 mb-1">
            <div className="w-10 h-10 rounded-xl bg-orange-500/20 flex items-center justify-center">
              <Wrench className="w-5 h-5 text-orange-400" />
            </div>
            <h1 className="text-2xl font-bold text-white">Patch Management</h1>
          </div>
          <p className="text-gray-500">Windows · Linux · Third-party applications · Compliance reporting</p>
        </div>
        <div className="flex gap-2">
          <div className="flex items-center gap-2 px-3 py-1.5 bg-red-500/10 border border-red-500/20 rounded-lg">
            <div className="w-2 h-2 rounded-full bg-red-400" />
            <span className="text-xs text-red-300 font-medium">3 Critical CVEs</span>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-4 gap-4">
        {STATS.map((stat) => {
          const Icon = stat.icon;
          return (
            <div key={stat.label} className="bg-white border border-gray-200 rounded-xl p-4">
              <div className={`w-9 h-9 rounded-lg ${stat.bg} flex items-center justify-center mb-3`}>
                <Icon className={`w-4 h-4 ${stat.color}`} />
              </div>
              <div className="text-2xl font-bold text-white">{stat.value}</div>
              <div className="text-xs text-gray-400 mt-0.5">{stat.label}</div>
            </div>
          );
        })}
      </div>

      {/* Compliance by OS */}
      <div className="bg-white border border-gray-200 rounded-xl p-5">
        <h3 className="text-sm font-semibold text-white mb-4">Patch Compliance by Platform</h3>
        <div className="space-y-3">
          {[
            { os: 'Windows Server 2022', servers: 312, compliant: 98, color: 'bg-blue-500' },
            { os: 'Windows Server 2019', servers: 489, compliant: 95, color: 'bg-blue-400' },
            { os: 'RHEL 8/9', servers: 287, compliant: 91, color: 'bg-orange-500' },
            { os: 'Ubuntu 22.04 LTS', servers: 196, compliant: 96, color: 'bg-orange-400' },
          ].map((item) => (
            <div key={item.os} className="flex items-center gap-4">
              <span className="text-sm text-gray-600 w-48 flex-shrink-0">{item.os}</span>
              <div className="flex-1 h-2 bg-gray-100 rounded-full">
                <div className={`h-full rounded-full ${item.color}`} style={{ width: `${item.compliant}%` }} />
              </div>
              <span className="text-sm font-semibold text-white w-12 text-right">{item.compliant}%</span>
              <span className="text-xs text-gray-400 w-20 text-right">{item.servers} servers</span>
            </div>
          ))}
        </div>
      </div>

      {/* Upcoming Patch Windows */}
      <div className="bg-white border border-gray-200 rounded-xl p-5">
        <h3 className="text-sm font-semibold text-white mb-4 flex items-center gap-2">
          <Clock className="w-4 h-4 text-gray-400" />
          Upcoming Patch Windows
        </h3>
        <div className="space-y-2">
          {[
            { date: 'Mar 18, 2026 – 22:00', group: 'Non-Production Windows', count: 203, status: 'Scheduled' },
            { date: 'Mar 19, 2026 – 01:00', group: 'Production Windows Wave 1', count: 156, status: 'Pending Approval' },
            { date: 'Mar 21, 2026 – 23:00', group: 'Linux Servers (RHEL/Ubuntu)', count: 287, status: 'Scheduled' },
            { date: 'Mar 25, 2026 – 22:00', group: 'Third-party Apps (Java, Chrome)', count: 948, status: 'Scheduled' },
          ].map((pw, i) => (
            <div key={i} className="flex items-center gap-3 py-2 border-b border-gray-200 last:border-0">
              <Clock className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" />
              <div className="flex-1 min-w-0">
                <div className="text-sm text-white">{pw.group}</div>
                <div className="text-xs text-gray-400">{pw.date} · {pw.count} targets</div>
              </div>
              <span className={`px-2 py-0.5 rounded text-[10px] font-semibold ${
                pw.status === 'Pending Approval' ? 'bg-amber-500/20 text-amber-300' : 'bg-emerald-500/20 text-emerald-300'
              }`}>{pw.status}</span>
            </div>
          ))}
        </div>
      </div>

      <div>
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-white">Automation Blueprints</h2>
          <div className="flex gap-2">
            {categories.map((cat) => (
              <button key={cat} onClick={() => setActiveCategory(cat)} className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${activeCategory === cat ? 'bg-orbayx-600/30 text-orbayx-700 border border-orbayx-500/30' : 'text-gray-500 hover:text-gray-900 hover:bg-gray-50'}`}>
                {cat}
              </button>
            ))}
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {filtered.map((bp) => {
            const Icon = bp.icon;
            return (
              <div key={bp.id} className="bg-white border border-gray-200 rounded-xl p-5 hover:border-orange-500/30 transition-all group cursor-pointer">
                <div className="flex items-start justify-between mb-3">
                  <div className="w-10 h-10 rounded-lg bg-orange-500/15 flex items-center justify-center">
                    <Icon className="w-5 h-5 text-orange-400" />
                  </div>
                  <span className={`px-2 py-0.5 rounded text-[10px] font-semibold uppercase ${DIFFICULTY_COLORS[bp.difficulty]}`}>{bp.difficulty}</span>
                </div>
                <h3 className="text-sm font-semibold text-white mb-1 group-hover:text-orange-300 transition-colors">{bp.name}</h3>
                <p className="text-xs text-gray-400 mb-3">{bp.description}</p>
                <div className="flex items-center justify-between text-xs text-gray-400">
                  <span className="flex items-center gap-1"><Clock className="w-3 h-3" />{bp.duration}</span>
                  <span>⭐ {bp.rating} · {bp.usageCount.toLocaleString()} runs</span>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
